import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import useChat from '../hooks/useChatContext';
import useActions from '../hooks/useActions';
import ChatInput from '../components/ChatInput';
import MessageList from '../components/MessageList';
import SuggestionsBar from '../components/SuggestionsBar';
import './Chat.css';

const API_BASE = (process.env.REACT_APP_API_URL || "https://ukd45lve3kvj45tahklem6d67q0fbbxm.lambda-url.us-west-2.on.aws/api/chat").replace('/api/chat', '');

export default function ChatPage() {
    const auth = useAuth();
    const navigate = useNavigate();
    const chat = useChat();
    const { createAction } = useActions();
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const [onboarded, setOnboarded] = useState(null);
    const [showReport, setShowReport] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [reportSent, setReportSent] = useState(false);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [chat.messages, chat.busy]);

    // Load profile (onboarding status + current session messages) on mount
    useEffect(() => {
        (async () => {
            try {
                const token = await auth?.getIdToken();
                if (!token) return;
                const res = await fetch(`${API_BASE}/api/profile`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                setOnboarded(!!data.onboarding?.completed);
                if (auth.sessionRestored && Array.isArray(data.messages)) chat.seedMessages(data.messages);
            } catch (e) {
                setOnboarded(null);
            }
        })();
    }, [auth?.user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Starter suggestions: fetch when the user hasn't sent a message yet
    useEffect(() => {
        if (chat.messages.some(m => m.role === 'user')) return;
        let cancelled = false;
        (async () => {
            try {
                const token = await auth?.getIdToken();
                if (!token) return;
                const res = await fetch(`${API_BASE}/api/suggestions`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (cancelled) return;
                if (!res.ok) {
                    if (res.status !== 401) chat.setSuggestionsError(true);
                    chat.setSuggestions([]);
                    return;
                }
                const data = await res.json();
                if (cancelled) return;
                chat.setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
                chat.setSuggestionsError(!!data.error);
            } catch (e) {
                if (cancelled) return;
                chat.setSuggestions([]);
                chat.setSuggestionsError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [chat.messages.length, auth]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-dismiss the failure toast after 4s
    useEffect(() => {
        if (!chat.suggestionsError) return;
        const t = setTimeout(() => chat.setSuggestionsError(false), 4000);
        return () => clearTimeout(t);
    }, [chat.suggestionsError]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="chat-app">
            <header className="chat-topbar">
                <div className="chat-topbar-brand">
                    <span className="chat-topbar-dot" />
                    InTension
                </div>
                <div className="chat-topbar-right">
                    <button className="chat-topbar-nav" onClick={() => navigate('/how-it-works')}>
                        How it works
                    </button>
                    <button className="chat-topbar-nav" onClick={() => navigate('/profile')}>
                        Profile
                    </button>
                    <button className="chat-topbar-nav" onClick={() => navigate('/actions')}>
                        Actions
                    </button>
                    <button className="chat-topbar-nav" onClick={() => navigate('/settings')}>
                        Settings
                    </button>
                    {chat.messages.length > 0 && (
                        <button className="chat-topbar-nav report" onClick={() => setShowReport(true)}>
                            Report
                        </button>
                    )}
                    <button className="chat-topbar-signout" onClick={() => { chat.reset(); auth.signOut(); }}>
                        Sign out
                    </button>
                </div>
            </header>

            <div className="chat-thread" ref={scrollRef}>
                {onboarded === false && (
                    <div className="onboard-banner">
                        <span>Complete onboarding so InTension has a better idea of who you are</span>
                        <button onClick={() => navigate('/onboard')}>Set up</button>
                    </div>
                )}
                {chat.messages.length === 0 ? (
                    <div className="chat-empty">
                        <h1>What's on your mind?</h1>
                    </div>
                ) : (
                    <MessageList
                        messages={chat.messages}
                        busy={chat.busy}
                        onAcceptAction={createAction}
                    />
                )}
            </div>

            <div className="chat-input-area">
                <div className="chat-input-wrap">
                    <SuggestionsBar
                        suggestions={chat.busy ? [] : chat.suggestions}
                        disabled={chat.busy}
                        onPick={(text) => inputRef.current?.prefill(text)}
                    />
                    <ChatInput
                        ref={inputRef}
                        onSend={chat.send}
                        disabled={chat.busy}
                        placeholder={chat.messages.length === 0 ? "Start typing..." : "Reply..."}
                    />
                </div>
            </div>

            {chat.suggestionsError && (
                <div className="suggestions-toast" role="status">
                    <span>Couldn't load suggestions</span>
                    <button
                        type="button"
                        className="suggestions-toast-close"
                        aria-label="Dismiss"
                        onClick={() => chat.setSuggestionsError(false)}
                    >
                        &times;
                    </button>
                </div>
            )}

            {showReport && (
                <div className="report-backdrop" onClick={() => { setShowReport(false); setReportReason(''); setReportSent(false); }}>
                    <div className="report-modal" onClick={e => e.stopPropagation()}>
                        {reportSent ? (
                            <>
                                <h3>Report submitted</h3>
                                <p>Thanks for letting us know. We'll review this conversation.</p>
                                <button className="report-btn close" onClick={() => { setShowReport(false); setReportReason(''); setReportSent(false); }}>
                                    Close
                                </button>
                            </>
                        ) : (
                            <>
                                <h3>Report this conversation</h3>
                                <p>This will send the current session for review. It will be automatically deleted after 7 days.</p>
                                <textarea
                                    className="report-textarea"
                                    placeholder="What went wrong? (optional)"
                                    value={reportReason}
                                    onChange={e => setReportReason(e.target.value)}
                                    rows={3}
                                />
                                <div className="report-actions">
                                    <button className="report-btn submit" onClick={async () => {
                                        try {
                                            const token = await auth.getIdToken();
                                            await fetch(`${API_BASE}/api/report`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                body: JSON.stringify({
                                                    reason: reportReason,
                                                    conversation: chat.messages.map(m => ({ role: m.role, content: m.content || '' })),
                                                }),
                                            });
                                            setReportSent(true);
                                        } catch (e) {
                                            console.error('Report failed', e);
                                        }
                                    }}>
                                        Submit report
                                    </button>
                                    <button className="report-btn cancel" onClick={() => { setShowReport(false); setReportReason(''); }}>
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
